/**
 * Client helper to obtain an iB2B OBO trust token from the BFF (`POST /api/trust-token`).
 * Alternative to cidToken.js; no payload binding. See src/server/cid/generateTrustToken.js.
 */

import { getStoredAccessToken, getStoredIdToken, getUserEmail, getUserCuid } from './session';
import { refreshCognitoSession } from './refresh';

/** Resolve the trust-token endpoint, preserving any leading context path. */
const resolveTrustTokenEndpoint = () => {
  if (typeof window === 'undefined' || !window.location) {
    return '/api/trust-token';
  }

  const { hostname, pathname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/api/trust-token';
  }

  const firstPathSegment = (pathname || '').split('/').filter(Boolean)[0];
  if (firstPathSegment && firstPathSegment !== 'api') {
    return `/${firstPathSegment}/api/trust-token`;
  }

  return '/api/trust-token';
};

export const TRUST_TOKEN_ENDPOINT = resolveTrustTokenEndpoint();

// The BFF verifies the id_token, binds it to the access_token, and cross-checks the
// forwarded email/cuid against the verified id_token claims before issuing the trust token.
const postTrustTokenRequest = (accessToken, idToken, email, cuid) =>
  fetch(TRUST_TOKEN_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(email ? { 'X-HSBC-User-Email': email } : {}),
      ...(cuid ? { 'X-HSBC-User-Cuid': cuid } : {}),
    },
    body: JSON.stringify({ accessToken, idToken }),
  });

const parseTrustTokenResponse = async (response) => {
  let responseText = '';
  try {
    responseText = await response.text();
  } catch {
    return {};
  }

  if (!responseText) {
    return {};
  }

  let parsed;
  try {
    parsed = JSON.parse(responseText);
  } catch {
    return {};
  }

  if (!parsed || typeof parsed !== 'object') {
    return {};
  }

  return {
    token: typeof parsed.token === 'string' ? parsed.token : undefined,
    error: typeof parsed.error === 'string' ? parsed.error : undefined,
  };
};

/** Request an iB2B OBO trust token; refreshes Cognito once on 401 and retries. */
export const requestTrustToken = async () => {
  let accessToken = getStoredAccessToken();
  let idToken = getStoredIdToken();
  if (!accessToken) {
    throw new Error('No Cognito access token available; cannot request trust token.');
  }
  if (!idToken) {
    throw new Error('No Cognito id token available; cannot request trust token.');
  }

  let response = await postTrustTokenRequest(accessToken, idToken, getUserEmail(), getUserCuid());

  // Refresh once on 401 and retry.
  if (response.status === 401) {
    await refreshCognitoSession();
    accessToken = getStoredAccessToken();
    idToken = getStoredIdToken();
    if (accessToken && idToken) {
      response = await postTrustTokenRequest(accessToken, idToken, getUserEmail(), getUserCuid());
    }
  }

  const data = await parseTrustTokenResponse(response);

  if (!response.ok || !data.token) {
    throw new Error((data && data.error) || `Trust token request failed (${response.status}).`);
  }

  return data.token;
};

export default requestTrustToken;
